<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

/**
 * Top progress bar widoczny podczas nawigacji.
 * Eliminuje "czarny ekran" podczas ladowania chunkow lazy-loaded views.
 *
 * 3 stany:
 * - hidden (idle)
 * - loading (200ms+ od start navigation, animowany progress 0→90%)
 * - complete (zaladowane, finish do 100% i fade out)
 */
const router = useRouter();

const visible = ref(false);
const progress = ref(0);
let startTimer: number | null = null;
let progressTimer: number | null = null;

function start() {
  if (startTimer != null) window.clearTimeout(startTimer);
  // Krotkie nawigacje (cached chunk) nie pokazuja paska — czekamy 120ms.
  startTimer = window.setTimeout(() => {
    visible.value = true;
    progress.value = 10;
    let target = 10;
    progressTimer = window.setInterval(() => {
      target = Math.min(target + (90 - target) * 0.12 + 1, 90);
      progress.value = target;
    }, 200);
  }, 120);
}

function done() {
  if (startTimer != null) {
    window.clearTimeout(startTimer);
    startTimer = null;
  }
  if (progressTimer != null) {
    window.clearInterval(progressTimer);
    progressTimer = null;
  }
  if (!visible.value) return;
  progress.value = 100;
  window.setTimeout(() => {
    visible.value = false;
    progress.value = 0;
  }, 220);
}

let removeBefore: (() => void) | null = null;
let removeAfter: (() => void) | null = null;
let removeError: (() => void) | null = null;

onMounted(() => {
  removeBefore = router.beforeEach((_to, _from, next) => {
    start();
    next();
  });
  removeAfter = router.afterEach(() => done());
  removeError = router.onError(() => done());
});

onUnmounted(() => {
  removeBefore?.();
  removeAfter?.();
  removeError?.();
  if (startTimer != null) window.clearTimeout(startTimer);
  if (progressTimer != null) window.clearInterval(progressTimer);
});
</script>

<template>
  <Transition name="progress-fade">
    <div
      v-if="visible"
      class="fixed top-0 left-0 right-0 z-[100] pointer-events-none"
      style="height: 2px"
    >
      <div
        class="h-full transition-[width] duration-200 ease-out"
        :style="{
          width: `${progress}%`,
          background:
            'linear-gradient(90deg, var(--color-accent), var(--color-product-light), var(--color-accent))',
          backgroundSize: '200% 100%',
          boxShadow: '0 0 8px var(--color-accent), 0 0 16px var(--color-accent)',
          animation: 'progress-shimmer 1.4s linear infinite',
        }"
      />
    </div>
  </Transition>
</template>

<style scoped>
.progress-fade-enter-active,
.progress-fade-leave-active {
  transition: opacity 0.2s ease;
}
.progress-fade-enter-from,
.progress-fade-leave-to {
  opacity: 0;
}

@keyframes progress-shimmer {
  0% {
    background-position: 0% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
